/**
 * Vista móvil para lista de productos del pedido/albarán
 * Cards responsive con información del producto
 */
import React, { useState, useContext } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Stack,
    Divider,
    Button
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import QrCodeIcon from "@mui/icons-material/QrCode";
import NumbersIcon from "@mui/icons-material/Numbers";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SellIcon from "@mui/icons-material/Sell";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import WarningIcon from "@mui/icons-material/Warning";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import { InfoField } from "../ui";
import { ModalAnadirEan, ModalConfirmacion } from "../modals";
import { StateContext } from "../../../../components/context/StateProvider";
import { getProductStateBadgeStyles } from "../../utils/orderStateColors";
import { BADGE_COLORS } from "../../../../components/ui/badgeColors";

const ProductosMobileList = ({
    productos = [],
    read = false,
    orderComplete = false,
    onForceCheck
}) => {
    const { getNombreAlmacen } = useContext(StateContext);
    const [confirmModal, setConfirmModal] = useState({ open: false, producto: null });

    const handleOpenConfirm = (producto) => {
        setConfirmModal({ open: true, producto });
    };

    const handleCloseConfirm = () => {
        setConfirmModal({ open: false, producto: null });
    };

    const handleConfirmForce = () => {
        if (confirmModal.producto && onForceCheck) {
            onForceCheck(confirmModal.producto);
        }
        handleCloseConfirm();
    };

    if (!productos || productos.length === 0) {
        return null;
    }

    const getEstadoLabel = (state) => {
        switch(parseInt(state, 10)) {
            case 0: return 'Pendiente';
            case 1: return 'Completado';
            case 2: return 'Falta';
            default: return 'Desconocido';
        }
    };

    // Obtener color de fondo según estado (igual que DataGrid)
    const getBackgroundColor = (state, read) => {
        if (read) return "#fff"; // Sin color si es solo lectura
        return parseInt(state, 10) === 1 ? BADGE_COLORS.GREEN.bg : BADGE_COLORS.RED.bg;
    };

    return (
        <Card
            sx={{
                backgroundColor: "#fff",
                borderRadius: 2,
                border: "1px solid #e9ecef",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
        >
            {/* Header del listado */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 2.5,
                    backgroundColor: "#f8f9fa",
                    borderBottom: "2px solid #e9ecef",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 36,
                        height: 36,
                        borderRadius: 1.5,
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.06)"
                    }}
                >
                    <Inventory2Icon sx={{ fontSize: 20, color: "#cc071e" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: "#6c757d",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            mb: 0.25
                        }}
                    >
                        Listado de productos
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: "#212529",
                            lineHeight: 1.2
                        }}
                    >
                        Productos del {read ? 'albarán' : 'pedido'}
                    </Typography>
                </Box>
            </Box>

            {/* Listado de productos */}
            <CardContent sx={{ p: 2 }}>
                <Stack spacing={2}>
                    {productos.map((producto, index) => (
                        <Card
                            key={producto.id || index}
                            sx={{
                                backgroundColor: getBackgroundColor(producto.state, read),
                                borderRadius: 2,
                                border: "1px solid #e9ecef",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                                overflow: "hidden",
                                transition: "all 0.2s",
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                    transform: "translateY(-2px)"
                                },
                            }}
                        >
                    {/* Header de la card */}
                    <Box
                        sx={{
                            p: 2,
                            backgroundColor: "#f8f9fa",
                            borderBottom: "1px solid #e9ecef",
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                                <SellIcon sx={{ fontSize: 18, color: "#495057", flexShrink: 0 }} />
                                <Typography
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: "#212529",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {producto.ref}
                                </Typography>
                            </Stack>
                            <Chip
                                label={getEstadoLabel(producto.state)}
                                size="small"
                                sx={{
                                    ...getProductStateBadgeStyles(producto.state, { height: 24, fontSize: 11 }),
                                    letterSpacing: "0.3px",
                                    flexShrink: 0
                                }}
                            />
                        </Stack>
                    </Box>

                    {/* Contenido de la card */}
                    <CardContent sx={{ p: 2 }}>
                        <Stack spacing={1.5}>
                            {/* Descripción */}
                            <InfoField compact icon={InventoryIcon} label="Producto">
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#212529" }}>
                                    {producto.name || '-'}
                                </Typography>
                            </InfoField>

                            <Divider sx={{ my: 0.5 }} />

                            {/* Código de barras */}
                            {producto.ean && (
                                <InfoField compact icon={QrCodeIcon} label="Código de barras">
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography sx={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: "#495057",
                                            fontFamily: "monospace",
                                            flex: 1
                                        }}>
                                            {Array.isArray(producto.ean) && producto.ean.length > 0
                                                ? producto.ean.map(item => `${item.BARRAS}-${item.UNIDADES}`).join(', ')
                                                : '-'}
                                        </Typography>
                                        {/* Botón añadir código de barras - solo si no está completado y no es read */}
                                        {producto.units > 0 && !producto.state && !read && !orderComplete && (
                                            <ModalAnadirEan
                                                productoId={producto.id}
                                                updated={true}
                                            />
                                        )}
                                    </Box>
                                </InfoField>
                            )}

                            {/* Unidades */}
                            <InfoField compact icon={NumbersIcon} label="Unidades">
                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                    <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#212529" }}>
                                        {producto.register || 0}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#495057" }}>
                                        / {producto.units || 0}
                                    </Typography>
                                </Box>
                            </InfoField>

                            {/* Ubicación */}
                            {producto.location && (
                                <InfoField compact icon={LocationOnIcon} label="Ubicación">
                                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#cc071e" }}>
                                        {producto.location}
                                    </Typography>
                                </InfoField>
                            )}

                            {/* Stock disponible (solo si no está puntuado) */}
                            {Array.isArray(producto.stock) && producto.stock.length > 0 && !producto.state && (
                                <>
                                    <Divider sx={{ my: 0.5 }} />
                                    <Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
                                            <WarehouseIcon sx={{ fontSize: 14, color: "#495057" }} />
                                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#6c757d", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                                                Stock disponible
                                            </Typography>
                                        </Box>
                                        <Stack spacing={0.5}>
                                            {producto.stock.map((item, stockIndex) => (
                                                <Box
                                                    key={stockIndex}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 1,
                                                        px: 1.5,
                                                        py: 0.75,
                                                        backgroundColor: "#f8f9fa",
                                                        borderRadius: 1,
                                                        border: "1px solid #e9ecef"
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#495057", flex: 1 }}>
                                                        {getNombreAlmacen(item.ALMACEN)}
                                                    </Typography>
                                                    <Chip
                                                        label={item.FINAL}
                                                        size="small"
                                                        sx={{
                                                            height: 22,
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            backgroundColor: "#fff",
                                                            border: "1px solid #dee2e6",
                                                            color: "#212529"
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                </>
                            )}

                            {/* Fecha de registro (solo si no es read) */}
                            {!read && producto.registerDate && (
                                <>
                                    <Divider sx={{ my: 0.5 }} />
                                    <InfoField compact icon={AccessTimeIcon} label="Registrado">
                                        <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#495057" }}>
                                            {producto.registerDate}
                                        </Typography>
                                    </InfoField>
                                </>
                            )}

                            {/* Serie (si tiene) */}
                            {producto.serie && (
                                <Box
                                    sx={{
                                        mt: 1,
                                        p: 1.5,
                                        backgroundColor: BADGE_COLORS.AMBER.bg,
                                        borderRadius: 1,
                                        border: `1px solid ${BADGE_COLORS.AMBER.border}`
                                    }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CheckCircleIcon sx={{ fontSize: 16, color: BADGE_COLORS.AMBER.text }} />
                                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: BADGE_COLORS.AMBER.text }}>
                                            Producto con serie
                                        </Typography>
                                    </Stack>
                                </Box>
                            )}

                            {/* Botón Forzar punteo */}
                            {!producto.state && !read && !orderComplete && onForceCheck && (
                                <>
                                    <Divider sx={{ my: 1 }} />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        onClick={() => handleOpenConfirm(producto)}
                                        sx={{
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: 13,
                                            color: BADGE_COLORS.AMBER.text,
                                            borderColor: BADGE_COLORS.AMBER.border,
                                            "&:hover": {
                                                borderColor: BADGE_COLORS.AMBER.text,
                                                backgroundColor: BADGE_COLORS.AMBER.bg,
                                            },
                                        }}
                                    >
                                        Forzar punteo
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            ))}
                </Stack>
            </CardContent>

            {/* Modal de confirmación: el mismo componente que usa la vista de escritorio */}
            <ModalConfirmacion
                show={confirmModal.open}
                onHide={handleCloseConfirm}
                onConfirm={handleConfirmForce}
                title="Confirmar forzado"
                titleMobile="Forzar"
                text="¿Está seguro de que desea <strong>forzar el punteo</strong> de este producto?"
                detalles={
                    confirmModal.producto
                        ? [
                            { label: "Referencia", value: confirmModal.producto.ref },
                            { label: "Producto", value: confirmModal.producto.name }
                        ]
                        : []
                }
                icon={WarningIcon}
                confirmIcon={WarningIcon}
                confirmLabel="Forzar punteo"
                confirmLabelMobile="Forzar"
            />
        </Card>
    );
};

export default ProductosMobileList;
